"use client";

import {
  type HTMLAttributes,
  type ReactNode,
  useEffect,
  useRef,
  type FC,
  type Ref,
} from "react";
import { motion, type MotionProps } from "motion/react";
import { isDocumentDefined } from "@huub-web/utils";

import { mergeRefs } from "../../utils";

export type FocusTrapProps = MotionProps &
  HTMLAttributes<HTMLDivElement> & {
    children: ReactNode;
    active?: boolean;
    onEscape?: () => void;
    mode?: "immediate" | "after";
    /**
     * Ref forwarded to the root element
     */
    ref?: Ref<HTMLDivElement>;
  };

/**
 * FocusTrap component that traps focus within its children.
 * Useful for modals, popups, and other components that need to keep focus within them.
 */
export const FocusTrap: FC<FocusTrapProps> = ({
  children,
  active = true,
  mode = "immediate",
  onEscape,
  ref,
  ...props
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const firstFocusableRef = useRef<HTMLElement | null>(null);
  const lastFocusableRef = useRef<HTMLElement | null>(null);
  const onEscapeRef = useRef(onEscape);
  useEffect(() => {
    onEscapeRef.current = onEscape;
  }, [onEscape]);

  // Handle Escape key at document level (works even without focus)
  useEffect(() => {
    if (!active) {
      return;
    }

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onEscapeRef.current?.();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [active]);

  // Handle focus trapping and auto-focus
  useEffect(() => {
    if (!active || !containerRef.current) {
      return;
    }

    const container = containerRef.current;

    // Find all focusable elements
    const updateFocusableElements = () => {
      const focusableElements = container.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );

      if (focusableElements.length > 0) {
        firstFocusableRef.current = focusableElements[0] as HTMLElement;
        lastFocusableRef.current = focusableElements[
          focusableElements.length - 1
        ] as HTMLElement;
      }

      return focusableElements;
    };

    const focusableElements = updateFocusableElements();

    // Only auto-focus in immediate mode
    if (
      mode === "immediate" &&
      focusableElements.length > 0 &&
      firstFocusableRef.current
    ) {
      firstFocusableRef.current.focus();
    }

    // Handle Tab at document level to trap focus
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isDocumentDefined() || e.key !== "Tab") {
        return;
      }

      // Re-query focusable elements in case DOM changed
      updateFocusableElements();

      const isWithinContainer = container.contains(
        document.activeElement as Node,
      );

      // If focus is outside container, bring it in on Tab
      if (!isWithinContainer) {
        e.preventDefault();
        if (e.shiftKey) {
          lastFocusableRef.current?.focus();
        } else {
          firstFocusableRef.current?.focus();
        }
        return;
      }

      // Trap focus within container
      if (e.shiftKey) {
        if (document.activeElement === firstFocusableRef.current) {
          e.preventDefault();
          lastFocusableRef.current?.focus();
        }
      } else {
        if (document.activeElement === lastFocusableRef.current) {
          e.preventDefault();
          firstFocusableRef.current?.focus();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [active, mode]);

  return (
    <motion.div ref={mergeRefs(containerRef, ref)} data-focus-trap {...props}>
      {children}
    </motion.div>
  );
};
